import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, Briefcase, Stack } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { _resetHrCatalogCache } from "@/components/CatalogCombobox";

function CatalogColumn({ kind, title, Icon, placeholder }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const load = useCallback(
    () => api.get(`/hr/catalog/${kind}`).then((r) => setRows(r.data || [])).catch(() => {}),
    [kind]
  );
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const name = draft.trim();
    if (!name) return;
    try {
      await api.post(`/hr/catalog/${kind}`, { name });
      setDraft("");
      _resetHrCatalogCache(kind);
      toast.success("Agregado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar del catálogo?")) return;
    try {
      await api.delete(`/hr/catalog/${kind}/${id}`);
      _resetHrCatalogCache(kind);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid={`hr-catalog-${kind}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={16} weight="duotone" className="text-blue-900" />
        <Label className="overline-label">{title}</Label>
        <span className="text-[10px] text-slate-400">({rows.length})</span>
      </div>
      <div className="flex gap-2 mb-2">
        <Input className="h-8" placeholder={placeholder} value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid={`hr-catalog-${kind}-input`} />
        <Button size="sm" className="h-8 bg-slate-900 text-white" onClick={add}
          disabled={!draft.trim()} data-testid={`hr-catalog-${kind}-add`}><Plus size={13} /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-60 overflow-y-auto">
        {rows.map((r) => (
          <span key={r.id}
            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1 py-0.5 text-xs"
            data-testid={`hr-catalog-${kind}-item-${r.id}`}>
            {r.name}
            <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600"
              data-testid={`hr-catalog-${kind}-del-${r.id}`}><Trash size={11} /></button>
          </span>
        ))}
        {rows.length === 0 && <span className="text-xs text-slate-400">Vacío</span>}
      </div>
    </div>
  );
}

export default function CargosAreasPanel() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="settings-hr-catalog">
      <h3 className="font-heading font-bold text-slate-800 uppercase text-xs tracking-widest">Cargos y Áreas (RR. HH.)</h3>
      <p className="text-[11px] text-slate-500 mb-3">
        Listas precargadas para homologar los campos <b>Cargo</b> y <b>Área</b> al registrar colaboradores.
        Lo que agregues aquí aparece como opción en el formulario de "Nuevo colaborador".
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        <CatalogColumn kind="cargo" title="Cargos" Icon={Briefcase} placeholder="Ej. Perforista" />
        <CatalogColumn kind="area" title="Áreas" Icon={Stack} placeholder="Ej. Perforación" />
      </div>
    </div>
  );
}
